import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { FC, useEffect } from 'react';
import { CREATE_DATA_SOURCE_FILE, CREATE_DATA_SOURCE_FILE_STEPS } from './constants';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { clearStashedDataSource } from '@/features/dataSources/dataSourcesSlice';
import { RouteConstants } from '@/features/router/RouteConstants';
import {
  FileDataSourceColumnMapStep,
  FileDataSourceConfigurationCompletedStep,
  FileDataSourceCSVHeaderStep,
  FileDataTypeStep,
  NameAndFileDataSourceStep,
} from './steps';
import { useAppSelector } from '@/reducers';
import { EStatus } from '@/models/fileUpload';
import { FileDataSourceProgressBar } from './commons/FileDataSourceProgressBar/FileDataSourceProgressBar';
import { Loadable } from '@/components/loaders';
import { useGetDataSourceTypesQuery } from '@/services/dataSources';
import { DataSource, DataSourceFileConfig, DataSourceIDEnum } from '@/models/dataSource';

type CreateDataSourceFileContentProps = {
  dataSourceId: DataSourceIDEnum;
  modalProps?: {
    onSuccess: (dataSource: DataSource) => void;
    onSuccessAndContinue?: (dataSource: DataSource) => void;
    closeModal: VoidFunction;
  };
};

export const CreateDataSourceFileContent: FC<CreateDataSourceFileContentProps> = ({ dataSourceId, modalProps }) => {
  const dispatch = useDispatch();
  const { steps, currentStepId, currentStepIndex, goToNextStep, goToPreviousStep, completeCurrentStep } = useCreateStepper(CREATE_DATA_SOURCE_FILE_STEPS);

  const history = useHistory();

  const { isFetching, data: sources } = useGetDataSourceTypesQuery();
  const dataSourceType = sources?.find((source) => source.id === dataSourceId);
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;

  const fileStatus = useAppSelector((state) => state.fileUploading.status);
  const fileInfo = useAppSelector((state) => state.fileUploading.fileInfo);

  useEffect(() => {
    dispatch(clearStashedDataSource());
  }, []);

  const handleNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const handlePrevStep = () => {
    goToPreviousStep();
  };

  const handleCancel = () => {
    if (modalProps) {
      modalProps.closeModal();
    } else {
      history.push(`${RouteConstants.dataSources}?catalog`);
    }
  };

  const handleCloseAndContinue = () => {
    if (modalProps && dataSource) {
      if (modalProps.onSuccessAndContinue) {
        modalProps.onSuccessAndContinue(dataSource as DataSource);
      } else {
        modalProps.onSuccess(dataSource as DataSource);
      }
    } else {
      history.push(RouteConstants.audiencesCreate);
    }
  };

  const handleCloseOnly = () => {
    if (modalProps && dataSource) {
      modalProps.onSuccess(dataSource as DataSource);
    } else {
      history.push(RouteConstants.dataSources);
    }
  };

  const FileDataSourceProgressBarSection = (
    <div className='flex grow flex-col justify-end'>
      <FileDataSourceProgressBar
        className='max-w-80'
        fileUpload={
          fileStatus?.uploadingStatus !== undefined
            ? {
                status: fileStatus.uploadingStatus,
                percentage: fileStatus.progress || { total: 0, loaded: 0, percentageCompleted: 0 },
              }
            : {
                status: EStatus.NOT_SELECTED,
                percentage: { total: 0, loaded: 0, percentageCompleted: 0 },
              }
        }
        filename={fileInfo?.filename}
      />
    </div>
  );

  const stepTemplate = () => {
    switch (currentStepId) {
      case CREATE_DATA_SOURCE_FILE.NAME_AND_FILE:
        return <NameAndFileDataSourceStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} dataSourceType={dataSourceType} />;
      case CREATE_DATA_SOURCE_FILE.HEADER:
        return (
          <>
            <FileDataSourceCSVHeaderStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />
            {FileDataSourceProgressBarSection}
          </>
        );
      case CREATE_DATA_SOURCE_FILE.DATA_TYPE:
        return (
          <>
            <FileDataTypeStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />
            {FileDataSourceProgressBarSection}
          </>
        );
      case CREATE_DATA_SOURCE_FILE.MAP_COLUMNS:
        return (
          <>
            <FileDataSourceColumnMapStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} isToCheckUploadSuccess />
            {FileDataSourceProgressBarSection}
          </>
        );
      case CREATE_DATA_SOURCE_FILE.CONFIRMATION:
        return (
          <FileDataSourceConfigurationCompletedStep
            onInit={completeCurrentStep}
            onNextStep={handleCloseAndContinue}
            onPrevStep={handleCloseOnly}
            onCancel={handleCancel}
            isModal={!!modalProps}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />

      <Loadable isLoading={isFetching}>{stepTemplate()}</Loadable>
    </>
  );
};
